// Face-gallery side of the "בעריכה" holds: the held list, the guest's own hold, and the Next-click acquire.
//
// * The hold is acquired at the guest's NEXT click (never on selection). The persisted state
//   sessionStorage["faceClaim:<event>"] = { token, faces, acqRid, acqKey, acqPrior, approveRid, approveAt, guest }
//   survives a same-guest continuation reload at the NEXT step; the acquire request id AND its prior token are
//   persisted BEFORE sending so a retry/reload repeats the exact same request (the server replays the same
//   token) instead of creating a second hold or a request_mismatch. Coming BACK to this gallery releases the
//   hold (see the mount effect); the following Next acquires afresh.
// * No heartbeat: the lease is renewed only after real activity and at least RENEW_MIN_MS after the last
//   renew, through the flow-wide renewal owner (claimRenewal.js, shared with the later pages). A renew/status
//   410 means the hold is gone; it is never revived.
// * Every async continuation re-checks the captured event (scope OBJECT identity, so A -> B -> A drops the
//   first A) and the mounted flag before touching state, storage or navigation.
import { useCallback, useEffect, useRef, useState } from "react";
import { getFaceClaimEvent } from "./faceClaimEvent";
import {
  acquireFaces,
  clearClaimState,
  currentGuestId,
  currentGuestSource,
  fetchHeld,
  newRequestId,
  readClaimState,
  releaseClaim,
  writeClaimState,
} from "./faceClaimClient";
import { isFlowExpired } from "./useFlowInactivity";
import { noteHoldRenewed, renewStoredHold, RENEW_MIN_MS } from "./claimRenewal";
import { faceKey, parseHeldBody, HELD_EDITING } from "./faceHeld";

export { RENEW_MIN_MS };
// The held list is re-read this often while the gallery is visible (plus at once on focus / online / pageshow /
// visibility and after every action): bounded, prompt, no new socket infrastructure.
export const HELD_POLL_MS = 3000;
const HELD_REQUEST_TIMEOUT_MS = 8000;
const ACTIVITY_EVENTS = ["pointerdown", "touchstart", "keydown"];
// eventName -> number of gallery instances mounted so far (see the mount effect).
const galleryGenerations = new Map();

export const MSG_HELD_BY_OTHER = "חלק מהפרצופים תפוסים כרגע אצל אורח אחר";
export const MSG_LOST = "הבחירה פגה, נא לבחור שוב";
export const MSG_RELEASED = "הבחירה שוחררה על ידי הצוות, נא לבחור ראש שוב";
export const MSG_RELEASE_UNCONFIRMED = "לא ניתן היה לשחרר את הראשים שבחרתם, הם יתפנו מעצמם בעוד כמה דקות";
export const MSG_NETWORK ="בעיית תקשורת, נסו שוב";
export const MSG_PROCESSING_FACE = "הראש כבר בעיבוד, נא לבחור ראש אחר";
export const MSG_UNAVAILABLE = "לא ניתן להמשיך כרגע, נסו שוב בעוד רגע";
export const MSG_AVAILABILITY_ERROR = "לא ניתן לבדוק אילו ראשים תפוסים. מנסים שוב...";

// Why NEXT did not proceed (a non-proceeding acquireForSelection result) -> the Hebrew explanation.
export const messageForAcquire = (result) => {
  switch (result && result.status) {
    case "held":
      return MSG_HELD_BY_OTHER;
    case "expired":
      return MSG_LOST;
    case "revoked":
      return MSG_RELEASED;
    case "processing":
      return MSG_PROCESSING_FACE;
    case "network":
      return MSG_NETWORK;
    case "no_guest": // no registered guest id: nothing was sent
    default:
      return MSG_UNAVAILABLE;
  }
};

// adminPreview: the console mirror never claims anything.
// onLost(reason): the hold is gone (renew/status 410, other tab or the operator released it): caller clears its
//                 selection. reason: "revoked" (operator release) | "lost".
// onRestore(faces): returned from a later step: the hold is released, caller keeps/pre-selects these faces.
// onReleaseFailed(): that release could not be confirmed (the server lease ends the hold by itself).
export const useFaceClaim = ({ adminPreview, onLost, onRestore, onReleaseFailed }) => {
  const [held, setHeld] = useState([]);
  // heldStates: { [faceKey]: "editing" | "processing" } from the LAST successful read; availability says
  // whether that read can be trusted: "loading" (no answer yet) | "ok" | "error" (the last read failed).
  const [heldStates, setHeldStates] = useState({});
  const [availability, setAvailability] = useState("loading");
  const [mine, setMine] = useState([]);
  const eventRef = useRef(null);
  if (eventRef.current === null && !adminPreview) eventRef.current = getFaceClaimEvent();
  const event = eventRef.current;
  const mountedRef = useRef(true);
  const mountGenRef = useRef(0);
  const submittingRef = useRef(false);
  const callbacksRef = useRef({ onLost, onRestore, onReleaseFailed });
  callbacksRef.current = { onLost, onRestore, onReleaseFailed };

  // Hold-operation identity: bumped whenever the hold changes hands (acquire/swap installed a token, lose,
  // release/cancel). A reply for token T1 / an older operation must never touch the hold that replaced it
  // (T2) or restore a cancelled one, even inside the same event visit and a still-mounted hook.
  const holdOpRef = useRef(0);

  const live = useCallback(() => mountedRef.current && Boolean(event) && event.isCurrent(), [event]);

  const storedToken = useCallback(() => {
    const state = event ? readClaimState(event.eventName) : null;
    return (state && state.token) || null;
  }, [event]);

  // The reply still belongs to the hold it was issued for: same visit, same operation, same stored token.
  const stillHolds = useCallback(
    (op, token) => live() && holdOpRef.current === op && storedToken() === (token || null),
    [live, storedToken]
  );

  const lose = useCallback(
    (reason = "lost") => {
      if (!live()) return;
      holdOpRef.current += 1;
      clearClaimState(event.eventName);
      setMine([]);
      const { onLost: handler } = callbacksRef.current;
      if (handler) handler(reason);
    },
    [event, live]
  );

  // Held list (+ my own hold when a token is persisted). Re-read every HELD_POLL_MS while visible, on the
  // gallery's own cycle, on focus / reconnect and after actions. ONE source of truth: the server list.
  //
  // SINGLE-FLIGHT: at most one read is in the air. A call while a read for the same (operation, token) is in
  // flight JOINS it (timer / focus / online / gallery-cycle spam adds no request, and a slow-but-healthy reply
  // is never discarded for being older than a newer poll). A call after the hold changed hands (acquire / swap /
  // release: new operation or token) waits for the old read to settle -- whose answer is dropped by stillHolds --
  // and then reads again, so an explicit invalidation always outranks old data. The request itself is bounded by
  // HELD_REQUEST_TIMEOUT_MS, so a read always settles.
  const inflightRef = useRef(null);
  const returnReleaseRef = useRef(null); // the in-flight release of the hold left over from the next step
  const readHeld = useCallback(
    async (token, op) => {
      const result = await fetchHeld(event, token || undefined, { timeoutMs: HELD_REQUEST_TIMEOUT_MS });
      if (!stillHolds(op, token)) return; // stale (event, operation or token changed): no state/storage/nav
      const parsed = result.ok ? parseHeldBody(result.body) : null;
      if (!parsed) {
        // outage / 4xx / 5xx / malformed body: keep the last list but SAY the state is unknown, never "free"
        setAvailability("error");
        return;
      }
      setHeld(result.body.held);
      setHeldStates(parsed.states);
      setAvailability("ok");
      if (token) {
        const info = result.body.mine || {};
        if (info.code === "editing") {
          const faces = Array.isArray(info.faces) ? info.faces : [];
          setMine(faces);
        } else if (info.code === "revoked") {
          lose("revoked");
        } else if (info.code && info.code !== "processing") {
          lose();
        }
      }
    },
    [event, stillHolds, lose]
  );
  const refreshRef = useRef(null);
  const refreshHeld = useCallback(() => {
    if (!event || adminPreview) return Promise.resolve();
    // Hold-release on return still in the air: read the list only once it settled, so the guest's own heads are
    // not shown as another guest's hold (which would drop the kept selection).
    if (returnReleaseRef.current) {
      return returnReleaseRef.current.then(() => (live() ? refreshRef.current() : undefined));
    }
    const token = storedToken();
    const op = holdOpRef.current;
    const current = inflightRef.current;
    if (current) {
      if (current.op === op && current.token === token) return current.promise;
      // The hold changed hands: read again once the older read has settled (never two at once).
      return current.promise.then(() =>
        live() && !isFlowExpired() ? refreshRef.current() : undefined
      );
    }
    const entry = { op, token, promise: null };
    entry.promise = readHeld(token, op).finally(() => {
      if (inflightRef.current === entry) inflightRef.current = null;
    });
    inflightRef.current = entry;
    return entry.promise;
  }, [event, adminPreview, storedToken, live, readHeld]);
  refreshRef.current = refreshHeld;

  // Returning to the gallery (in-app Back, browser/router back, reload) RELEASES this tab's own editing hold:
  // the guest is choosing again, so the heads must be free for others. The local selection is kept (UI only, it
  // implies no ownership) and the next Next acquires again. Only this tab's own token is released; the server
  // keeps processing/used faces untouched. Local state is cleared first, so renewal stops and a late reply for
  // the old token cannot touch whatever replaces it (holdOp bump).
  useEffect(() => {
    mountedRef.current = true;
    if (!event || adminPreview) return undefined;
    // A newer gallery instance for this event fences every still-pending acquire of the older ones (they must
    // not install a hold while the chooser is active again).
    mountGenRef.current = (galleryGenerations.get(event.eventName) || 0) + 1;
    galleryGenerations.set(event.eventName, mountGenRef.current);
    const state = readClaimState(event.eventName);
    if (state && state.token && !returnReleaseRef.current) {
      const token = state.token;
      holdOpRef.current += 1;
      clearClaimState(event.eventName);
      const faces = Array.isArray(state.faces) ? state.faces : [];
      const { onRestore: restore } = callbacksRef.current;
      if (faces.length && restore) restore(faces);
      const op = holdOpRef.current;
      // Resolves "processing" (the head is the approve's now) | "failed" (unconfirmed) | "revoked" (operator) |
      // "released".
      const pending = releaseClaim(event, token)
        .then((result) => {
          // The server never releases a processing claim: it is the approve's now, so keep its local record.
          if (result.ok && result.code === "processing") {
            if (event.isCurrent() && !readClaimState(event.eventName)) {
              writeClaimState(event.eventName, state);
              if (holdOpRef.current === op && live()) setMine(faces);
            }
            return "processing";
          }
          // 410 / 404 = already gone (expired, operator release): nothing left to release. An OPERATOR release
          // (claim_revoked, observed) is reported like any other revocation: the page sends the guest Home.
          if (result.status === 410 && result.code === "claim_revoked") {
            if (holdOpRef.current === op && live()) lose("revoked");
            return "revoked";
          }
          const gone = result.status === 410 || result.status === 404;
          if (!result.ok && !gone) {
            if (live()) {
              const { onReleaseFailed: failed } = callbacksRef.current;
              if (failed) failed();
            }
            return "failed";
          }
          return "released";
        })
        .finally(() => {
          if (returnReleaseRef.current === pending) returnReleaseRef.current = null;
        });
      returnReleaseRef.current = pending;
    }
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Renew only after real activity, never on a timer (guards and throttle: claimRenewal.js).
  useEffect(() => {
    if (!event || adminPreview) return undefined;
    const onActivity = async () => {
      if (!live()) return;
      const op = holdOpRef.current;
      const outcome = await renewStoredHold(event);
      if (!outcome) return;
      const { token, result } = outcome;
      // A T1 renew answering after a swap installed T2 must not expire/clear T2.
      if (!stillHolds(op, token)) return;
      if (result.status === 410) lose(result.code === "claim_revoked" ? "revoked" : "lost");
    };
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, { capture: true, passive: true })
    );
    const onVisible = () => {
      // An expired flow is the flow hook's business: no more polling once the 90 s deadline has passed.
      if (!document.hidden && !isFlowExpired()) refreshRef.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("online", onVisible);
    // Live status: first read at once, then every HELD_POLL_MS while the page is visible.
    onVisible();
    const poll = setInterval(onVisible, HELD_POLL_MS);
    return () => {
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onActivity, { capture: true })
      );
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("online", onVisible);
      clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Next click. Resolves { proceed, status, heldFaces }; `proceed` is true only on editing/shared.
  const acquireForSelection = useCallback(
    async (paths) => {
      if (!event || adminPreview) return { proceed: false, status: "no_event" };
      // The flow already expired (latched synchronously by the capture-phase activity handler of THIS
      // dispatch, or the stored deadline passed): never start new work, write nothing, send nothing.
      if (isFlowExpired()) return { proceed: false, status: "flow_expired" };
      if (!live()) return { proceed: false, status: "stale" };
      if (submittingRef.current) return { proceed: false, status: "busy" };
      const guest = currentGuestId();
      // Never an acquire without a guest: the hold would be bound to nobody and every approve of it
      // refused (guest_mismatch, 2026-10-18). Inside the event gate the id is always registered.
      if (!guest) return { proceed: false, status: "no_guest" };
      submittingRef.current = true;
      const op = holdOpRef.current;
      const guestSource = currentGuestSource();
      const myGen = mountGenRef.current;
      try {
        // The release of the hold left over from the next step must settle first: acquiring now would collide
        // with the guest's own old claim (faces_held) or install a token the release then clears.
        if (returnReleaseRef.current) {
          const outcome = await returnReleaseRef.current;
          if (!live() || holdOpRef.current !== op || isFlowExpired()) return { proceed: false, status: "stale" };
          // A processing head belongs to the approve now: never acquire over it.
          if (outcome === "processing") return { proceed: false, status: "processing" };
        }
        const key = [...paths].sort().join("|");
        const state = readClaimState(event.eventName) || {};
        // Request identity = the EXACT payload first sent: rid + faces + the PRIOR token it carried. A
        // successful acquire replaces state.token, so a replay (lost reply, reload) must carry the recorded
        // prior token (the server hashes it), never the newly issued one. Coming back to the gallery releases
        // the hold and the next Next starts a fresh request.
        if (state.acqKey !== key || !state.acqRid || !("acqPrior" in state)) {
          state.acqRid = newRequestId();
          state.acqKey = key;
          state.acqPrior = state.token || null;
        }
        writeClaimState(event.eventName, state); // persisted BEFORE the request
        const result = await acquireFaces(event, {
          faces: paths,
          requestId: state.acqRid,
          claimToken: state.acqPrior || undefined,
          userId: guest,
          guestSource,
        });
        // Not live any more (unmount / event change) or superseded (release/cancel, newer operation): do not
        // restore anything in this instance.
        if (!live() || holdOpRef.current !== op) {
          const lateToken = result.ok && result.code === "editing" ? result.body.claim_token : null;
          if (lateToken) {
            const current = event.isCurrent();
            if (current && holdOpRef.current === op && !isFlowExpired()) {
              // Ordinary same-event unmount (leaving the gallery): the continuation keeps the hold, but only
              // while the PERSISTED acquisition is still this exact request (id, guest, original payload
              // identity) and no newer gallery mounted meanwhile. A newer gallery means the guest is choosing
              // again (or the record was cancelled/replaced): release the late hold, and drop the request
              // identity so the next Next sends a fresh request instead of replaying the released one.
              const persisted = readClaimState(event.eventName);
              const sameRequest =
                persisted &&
                persisted.acqRid === state.acqRid &&
                persisted.acqKey === state.acqKey &&
                persisted.acqPrior === state.acqPrior;
              if (!persisted) {
                releaseClaim(event, lateToken, { keepalive: true });
              } else if (galleryGenerations.get(event.eventName) !== myGen) {
                if (persisted.token !== lateToken) releaseClaim(event, lateToken, { keepalive: true });
                if (sameRequest && !persisted.token) {
                  const rest = { ...persisted };
                  delete rest.acqRid;
                  delete rest.acqKey;
                  delete rest.acqPrior;
                  writeClaimState(event.eventName, rest);
                }
              } else if (
                persisted.guest === guest &&
                currentGuestId() === guest &&
                persisted.acqRid === state.acqRid &&
                persisted.acqKey === state.acqKey &&
                persisted.acqPrior === state.acqPrior &&
                persisted.token !== lateToken
              ) {
                writeClaimState(event.eventName, { ...persisted, token: lateToken, faces: paths });
              }
            } else if (!(current && storedToken() === lateToken)) {
              // Cancelled / timed out / left the event (and not adopted by the current continuation): do not
              // let the hold linger until its lease ends.
              releaseClaim(event, lateToken, { keepalive: true });
            }
          }
          return { proceed: false, status: "stale" };
        }

        // This instance is current, but the persisted acquisition may have been taken over while the reply was
        // pending: an older instance's late reply for the SAME request id released its token and dropped the
        // identity (a replay of that request answers the same, already released, token), or a newer scope/claim
        // replaced the record. Only a record still carrying exactly this request may be written.
        const persistedNow = readClaimState(event.eventName);
        const owned =
          !!persistedNow &&
          persistedNow.acqRid === state.acqRid &&
          persistedNow.acqKey === state.acqKey &&
          persistedNow.acqPrior === state.acqPrior;
        if (!owned && result.ok && (result.code === "editing" || result.code === "shared")) {
          const lateToken = result.code === "editing" ? result.body.claim_token : null;
          if (lateToken && !(persistedNow && persistedNow.token === lateToken)) {
            releaseClaim(event, lateToken, { keepalive: true });
          }
          return { proceed: false, status: "stale" };
        }

        if (result.ok && result.code === "editing" && result.body.claim_token) {
          holdOpRef.current += 1;
          writeClaimState(event.eventName, {
            ...state,
            token: result.body.claim_token,
            faces: paths,
          });
          noteHoldRenewed(event, result.body.claim_token);
          setMine(paths);
          return { proceed: true, status: "editing" };
        }
        if (result.ok && result.code === "shared") {
          holdOpRef.current += 1;
          writeClaimState(event.eventName, { ...state, token: null, faces: paths });
          return { proceed: true, status: "shared" };
        }
        if (result.status === 409 && result.code === "faces_held") {
          const heldFaces = Array.isArray(result.body.held_faces) ? result.body.held_faces : [];
          setHeld((current) => Array.from(new Set([...current, ...heldFaces])));
          setHeldStates((current) => {
            const next = { ...current };
            heldFaces.forEach((path) => {
              const key = faceKey(path);
              if (key && !next[key]) next[key] = HELD_EDITING;
            });
            return next;
          });
          return { proceed: false, status: "held", heldFaces };
        }
        if (result.status === 410) {
          // The previous hold (or this request id's lease) is gone and never revives: start clean.
          holdOpRef.current += 1;
          if (owned) {
            // never wipe a newer scope/claim
            clearClaimState(event.eventName);
            setMine([]);
          }
          return {
            proceed: false,
            status: result.code === "claim_revoked" ? "revoked" : "expired",
          };
        }
        if (result.code === "network") {
          return { proceed: false, status: "network" }; // retry reuses the persisted acqRid
        }
        if (result.status === 409 && result.code === "processing") {
          return { proceed: false, status: "processing" };
        }
        return { proceed: false, status: "error", code: result.code };
      } finally {
        submittingRef.current = false;
      }
    },
    // storedToken is a function of `event` alone (it reads the token at call time, so a token change does
    // not change its identity); listing it cannot re-create this callback on the hold's own updates.
    [event, adminPreview, live, storedToken]
  );

  // Explicit cancel / gallery Home: best-effort release of the editing hold; the server lease backstops.
  const releaseNow = useCallback(() => {
    // Only the active visit may touch the shared per-event record (a stale A visit must not clear a newer A).
    if (!event || !event.isCurrent()) return;
    holdOpRef.current += 1; // cancels any pending acquire: it can no longer restore this hold
    const state = readClaimState(event.eventName);
    clearClaimState(event.eventName);
    if (mountedRef.current) setMine([]);
    if (state && state.token) releaseClaim(event, state.token, { keepalive: true });
  }, [event]);

  // The server list minus my own hold = faces another guest holds (matched by normalized basename, see
  // faceHeld.js). Changing the selection after Back needs no special case: Next sends the persisted token with
  // the new set and the server swaps atomically.
  const mineKeys = new Set(mine.map(faceKey));
  const isBlockedByOther = (path) => {
    const key = faceKey(path);
    return Boolean(key) && Boolean(heldStates[key]) && !mineKeys.has(key);
  };
  const heldStateOf = (path) => heldStates[faceKey(path)] || null;
  const blockedByOthers = held.filter((path) => !mineKeys.has(faceKey(path)));
  // Changes only when the SET of blocked faces changes: the selection guard keys on it.
  const blockedSignature = Object.keys(heldStates)
    .filter((key) => !mineKeys.has(key))
    .sort()
    .join("|");

  return {
    held,
    mine,
    blockedByOthers,
    availability,
    isBlockedByOther,
    heldStateOf,
    blockedSignature,
    refreshHeld,
    acquireForSelection,
    releaseNow,
  };
};
